/**
 * TTS provider connections — Hume and Cartesia WebSocket clients.
 *
 * Each provider connects to the TTS API, sends text, receives audio chunks,
 * and feeds them to the OutputEngine as Float32Array.
 *
 * Text cleaning (stripForTTS) is applied here before sending.
 */

import { stripForTTS } from './stripForTTS';

// ═══════════════════════════════════════════
//  CARTESIA
// ═══════════════════════════════════════════

const CARTESIA_WS_URL = 'wss://api.cartesia.ai/tts/websocket';
const CARTESIA_VERSION = '2025-04-16';
const CARTESIA_MODEL = 'sonic-3';
const CARTESIA_SAMPLE_RATE = 44100;
const CARTESIA_TOKEN_REFRESH_MS = 4 * 60 * 1000;
const MIN_CHUNK_LEN = 80;
const SENTENCE_RE = /[.!?]\s+|\n/;

export function createCartesiaTTS(playAudio) {
  let ws = null;
  let contextId = null;
  let voiceId = '';
  let voiceLanguages = {};
  let speed = 1.0;
  let emotion = '';
  let language = '';
  let sentenceBuffer = '';
  let refreshTimer = null;
  let onNeedReconnect = null;

  function getVoiceId() {
    const picked = (language && voiceLanguages[language]) || voiceId;
    console.log('[CartesiaTTS] getVoiceId, lang:', language, 'voiceLanguages:', JSON.stringify(voiceLanguages), 'picked:', picked);
    return picked;
  }

  function isReady() { return ws && ws.readyState === WebSocket.OPEN; }

  function pushText(text) {
    console.log('[CartesiaTTS] pushText:', text.slice(0, 80));
    if (!isReady() || !contextId) return;
    const msg = {
      context_id: contextId, model_id: CARTESIA_MODEL, transcript: text,
      voice: { mode: 'id', id: getVoiceId() },
      output_format: { container: 'raw', encoding: 'pcm_f32le', sample_rate: CARTESIA_SAMPLE_RATE },
      continue: true,
    };
    if (language) msg.language = language;
    const gc = {};
    if (speed && speed !== 1.0) gc.speed = speed;
    if (emotion) gc.emotion = emotion;
    if (Object.keys(gc).length) msg.generation_config = gc;
    ws.send(JSON.stringify(msg));
  }

  return {
    name: 'cartesia',
    sampleRate: CARTESIA_SAMPLE_RATE,

    connect(tokenData, reconnectFn) {
      if (!tokenData.token) return;
      onNeedReconnect = reconnectFn;
      voiceId = tokenData.voice_id || '';
      voiceLanguages = tokenData.voice_languages || {};
      speed = tokenData.speed ?? 1.0;
      emotion = tokenData.emotion || '';

      ws = new WebSocket(`${CARTESIA_WS_URL}?${new URLSearchParams({
        cartesia_version: CARTESIA_VERSION,
        api_key: tokenData.token,
      })}`);
      contextId = crypto.randomUUID().replace(/-/g, '');

      ws.onopen = () => { console.log('[CartesiaTTS] WS opened'); this.onOpen?.(); };
      ws.onmessage = (ev) => {
        try {
          const m = JSON.parse(ev.data);
          if (m.type === 'chunk' && m.data) {
            const b = atob(m.data), a = new Uint8Array(b.length);
            for (let i = 0; i < b.length; i++) a[i] = b.charCodeAt(i);
            const float32 = new Float32Array(a.buffer, a.byteOffset, a.byteLength / 4);
            playAudio(float32);
          } else {
            console.log('[CartesiaTTS] ws msg:', m.type, m.status_code, m.error?.slice?.(0, 100));
          }
        } catch (_) {}
      };
      ws.onerror = (e) => { console.log('[CartesiaTTS] ws error', e); };
      ws.onclose = (e) => { console.log('[CartesiaTTS] ws closed, code:', e.code); ws = null; };

      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => { onNeedReconnect?.(); }, CARTESIA_TOKEN_REFRESH_MS);
    },

    disconnect() {
      if (refreshTimer) { clearTimeout(refreshTimer); refreshTimer = null; }
      if (ws) { ws.close(); ws = null; }
    },

    isReady,
    setLang(l) { language = l || ''; },

    bufferText(rawText) {
      if (!rawText) return;
      sentenceBuffer += rawText;
      while (true) {
        const m = sentenceBuffer.match(SENTENCE_RE);
        if (!m) break;
        const end = m.index + m[0].length;
        const chunk = sentenceBuffer.slice(0, end).trim();
        if (chunk.length >= MIN_CHUNK_LEN) {
          sentenceBuffer = sentenceBuffer.slice(end);
          pushText(stripForTTS(chunk, 'cartesia'));
        } else {
          break;
        }
      }
    },

    flushBuffer() {
      const r = sentenceBuffer.trim();
      sentenceBuffer = '';
      if (r.length >= 5) pushText(stripForTTS(r, 'cartesia'));
    },

    finishStream() {
      this.flushBuffer();
      if (!isReady() || !contextId) return;
      ws.send(JSON.stringify({
        context_id: contextId, model_id: CARTESIA_MODEL, transcript: '',
        voice: { mode: 'id', id: getVoiceId() },
        output_format: { container: 'raw', encoding: 'pcm_f32le', sample_rate: CARTESIA_SAMPLE_RATE },
        continue: false,
      }));
      contextId = crypto.randomUUID().replace(/-/g, '');
    },

    stop() {
      sentenceBuffer = '';
      if (isReady() && contextId) {
        try { ws.send(JSON.stringify({ cancel_context: contextId })); } catch (_) {}
        contextId = crypto.randomUUID().replace(/-/g, '');
      }
    },

    onOpen: null,
  };
}

// ═══════════════════════════════════════════
//  HUME
// ═══════════════════════════════════════════

const HUME_WS_URL = 'wss://api.hume.ai/v0/tts/stream/input';
const HUME_SAMPLE_RATE = 48000;
const HUME_TOKEN_REFRESH_MS = 25 * 60 * 1000;

export function createHumeTTS(playAudio) {
  let ws = null;
  let voiceId = '';
  let voiceLanguages = {};
  let voiceProvider = 'CUSTOM_VOICE';
  let speed = 1.0;
  let description = '';
  let language = '';
  let wordBuffer = '';
  let firstPush = true;
  let refreshTimer = null;
  let onNeedReconnect = null;

  function getVoiceId() {
    return (language && voiceLanguages[language]) || voiceId;
  }

  function isReady() { return ws && ws.readyState === WebSocket.OPEN; }

  function pushText(text) {
    if (!isReady()) return;
    const msg = { text };
    if (firstPush) {
      firstPush = false;
      const vid = getVoiceId();
      if (vid) {
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(vid);
        msg.voice = isUUID
          ? { id: vid, provider: voiceProvider }
          : { name: vid, provider: voiceProvider };
      }
      if (description) msg.description = description;
    }
    if (speed && speed !== 1.0) msg.speed = speed;
    ws.send(JSON.stringify(msg));
  }

  return {
    name: 'hume',
    sampleRate: HUME_SAMPLE_RATE,

    connect(tokenData, reconnectFn) {
      if (!tokenData.token) return;
      onNeedReconnect = reconnectFn;
      voiceId = tokenData.voice_id || '';
      voiceLanguages = tokenData.voice_languages || {};
      voiceProvider = tokenData.voice_provider || 'CUSTOM_VOICE';
      speed = tokenData.speed ?? 1.0;
      description = tokenData.description || '';
      firstPush = true;

      ws = new WebSocket(`${HUME_WS_URL}?${new URLSearchParams({
        access_token: tokenData.token,
        instant_mode: 'true',
        strip_headers: 'true',
        format_type: 'pcm',
        version: '2',
      })}`);
      ws.binaryType = 'arraybuffer';

      ws.onopen = () => { firstPush = true; this.onOpen?.(); };
      ws.onmessage = (ev) => {
        if (ev.data instanceof ArrayBuffer && ev.data.byteLength > 0) {
          // PCM 16-bit signed LE @ 48kHz → Float32
          const bytes = new Uint8Array(ev.data);
          const int16 = new Int16Array(bytes.buffer, bytes.byteOffset, bytes.byteLength / 2);
          const float32 = new Float32Array(int16.length);
          for (let i = 0; i < int16.length; i++) float32[i] = int16[i] / 32768;
          playAudio(float32);
        }
      };
      ws.onerror = () => {};
      ws.onclose = () => { ws = null; };

      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => { onNeedReconnect?.(); }, HUME_TOKEN_REFRESH_MS);
    },

    disconnect() {
      if (refreshTimer) { clearTimeout(refreshTimer); refreshTimer = null; }
      if (ws) { ws.close(); ws = null; }
    },

    isReady,
    setLang(l) { language = l || ''; },

    bufferText(rawText) {
      if (!rawText) return;
      wordBuffer += rawText;
      const sp = Math.max(wordBuffer.lastIndexOf(' '), wordBuffer.lastIndexOf('\n'));
      if (sp > 0) {
        const chunk = wordBuffer.slice(0, sp + 1);
        wordBuffer = wordBuffer.slice(sp + 1);
        const clean = stripForTTS(chunk, 'hume');
        if (clean?.trim()) pushText(clean);
      }
    },

    flushBuffer() {
      const r = wordBuffer.trim();
      wordBuffer = '';
      if (r) {
        const clean = stripForTTS(r, 'hume');
        if (clean?.trim()) pushText(clean);
      }
    },

    finishStream() {
      this.flushBuffer();
      if (!isReady()) return;
      ws.send(JSON.stringify({ flush: true }));
      firstPush = true;
    },

    stop() {
      wordBuffer = '';
      firstPush = true;
    },

    onOpen: null,
  };
}
